import React, { createContext, useContext, useCallback } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import { ACCEPTED_STATUS, ACTIVE_STATUS, VIEWED_STATUS } from './automation-reccomendation-constants';
import { emptyArray } from 'ui-components/default-props/empty';
import { useEnableAutomationRecommendations } from '../../feature-flags/hooks/use-enable-automation-recommendations';
import { useOnComponentMount } from '../../hooks/use-on-component-mount';
import { LogService } from '../../services/log-service';

const AutomationRecommendationsContext = createContext({});

export const AutomationRecommendationsContextProvider = ({ children }) => {
  const location = useLocation();
  const enabled = useEnableAutomationRecommendations();
  const shouldShowBasedOnLocation = ['/orders', '/dashboard', '/shipments'].some((path) => location.pathname.includes(path));
  const [loading, setLoading] = useAsyncSafeState(true);
  const [totalSavings, setTotalSavings] = useAsyncSafeState({
    value: 0,
    currencyCode: 'USD',
  });
  const [recommendations, setRecommendations] = useAsyncSafeState(emptyArray);
  const [salesHasBeenContacted, setSalesHasBeenContacted] = useAsyncSafeState(true);

  const getRecommendations = useCallback(
    async (silent = false) => {
      if (!enabled || !shouldShowBasedOnLocation) {
        return;
      }
      if (!silent) {
        setLoading(true);
      }
      axios
        .get('/automationRecommendations/')
        .then((res) => {
          const allRecommendations = res.data?.recommendations || [];
          const recommendationsToShow = allRecommendations.filter((r) =>
            [ACTIVE_STATUS, VIEWED_STATUS, ACCEPTED_STATUS].includes(r.statusId),
          );
          const sumOfSavings = recommendationsToShow.reduce((sum, i) => sum + i.thirtyDayProjectedSavings.value, 0);
          setRecommendations(recommendationsToShow);
          setSalesHasBeenContacted(allRecommendations.some((r) => r.statusId === ACCEPTED_STATUS));
          setTotalSavings({
            currencyCode: recommendationsToShow[0]?.thirtyDayProjectedSavings?.currencyCode || 'USD',
            value: sumOfSavings,
          });
          setLoading(false);
        })
        .catch((e) => {
          LogService.logError(new Error('Error getting automation recommendations', { cause: e }));
        });
    },
    [enabled, setLoading, setRecommendations, setSalesHasBeenContacted, setTotalSavings, shouldShowBasedOnLocation],
  );

  useOnComponentMount(() => {
    getRecommendations();
  });

  const value = {
    loading,
    totalSavings,
    recommendations,
    salesHasBeenContacted,
    setSalesHasBeenContacted,
  };
  return <AutomationRecommendationsContext.Provider value={value}>{!loading && children}</AutomationRecommendationsContext.Provider>;
};

export const useAutomationRecommendationsContext = () => useContext(AutomationRecommendationsContext);
